import { array } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { schedule } from '@ember/runloop';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { PopupEvent } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';
import { isAlive } from 'webapp/utils/common';

import joinClasses from 'webapp/helpers/join-classes';
import visibleCheckItem from 'webapp/modifiers/visible-check-item';
import BoardImagePreviewComponent from 'webapp/components/board/image-preview';
import type { Option } from 'webapp/components/boards/index/item-base';
import BoardsIndexItemBaseComponent from 'webapp/components/boards/index/item-base';
import CommonGenieButtonComponent from 'webapp/components/common/genie/button';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import DataPerformanceSummaryButtonComponent from 'webapp/components/data-performance/summary-button';
import EditableModelPropertyComponent from 'webapp/components/editable-model-property';
import FormCheckboxComponent from 'webapp/components/form/checkbox';
import OwnerSelectComponent from 'webapp/components/owner-select';
import SourceConnectButtonComponent from 'webapp/components/source/connect/button';
import SubmitButtonComponent from 'webapp/components/submit-button';
import TagContainerComponent from 'webapp/components/tag-container';
import TeamsViewSectionRestrictedIconComponent from 'webapp/components/teams/view/section/restricted-icon';
import ToolTipComponent from 'webapp/components/tool-tip';
import VerificationBadgeComponent from 'webapp/components/verification/badge';

export default class BoardsIndexGridItemComponent extends BoardsIndexItemBaseComponent {
	@service('popup') declare popupService: Service.Popup;

	@tracked overlayActive = false;
	@tracked isTitleTooLong = false;
	@tracked isDataPerformanceSummaryPopupOpen = false;

	get isSomethingOpen() {
		return this.overlayActive || this.isDataPerformanceSummaryPopupOpen;
	}

	get additionalOptions(): Option[] {
		const teamSectionItemOptions = this.teamSectionItemOptions,
			modelAdditionalOptions = this.args.model.getAdditionalOptions(
				!this.isTeamSectionItem,
				false,
				this.isTeamSectionItem,
			) as Option[];

		modelAdditionalOptions.push(...teamSectionItemOptions);

		return modelAdditionalOptions;
	}

	// Giving tooltip unique identifier that we can
	// later close specific instance of tooltip -
	// otherwise tooltip is over board permissions popup
	get privateTooltipClass() {
		return `private-${this.args.model.id}`;
	}

	get privateTooltipText() {
		return `
			<div class="text-center">
				<p>This dashboard is set to private and is only visible to you</p>
			</div>
		`;
	}

	get showResolveButton() {
		return !this.isDemoAccount && !this.isActiveUserViewer;
	}

	get checkVisibility() {
		return !!this.args.checkVisibility;
	}

	checkIfTitleTooLong(node: HTMLElement) {
		const containerWidth = node
				? Math.round(
						(node.querySelector('.info label')?.parentNode as HTMLElement)
							?.offsetWidth || 0,
					)
				: 0,
			textWidth = node
				? Math.round(
						node.querySelector<HTMLElement>('.info label strong')?.offsetWidth || 0,
					)
				: 0,
			paddingForIconAndEllipsisWidth = 20;

		this.isTitleTooLong = textWidth + paddingForIconAndEllipsisWidth >= containerWidth;
	}

	@action
	changeDataPerformanceSummaryPopupOpenState(isOpen: boolean) {
		this.isDataPerformanceSummaryPopupOpen = isOpen;
	}

	@action
	openAdditionalOptions(e: PointerEvent): void {
		if (!this.overlayActive) {
			const attachTo = (e.target as HTMLElement)?.closest<HTMLElement>('.context');

			const popup = this.popupService.createListPopup(
				attachTo,
				undefined,
				'board-options',
				true,
				true,
			);

			if (popup) {
				popup.open({
					itemsList: this.additionalOptions,
					hideDropdownSearch: true,
					doAction: (actionName: string) => {
						this.doOptionsAction(actionName);
					},
				});

				this.overlayActive = true;

				popup.on(PopupEvent.CLOSED, this, () => {
					if (isAlive(this)) {
						this.overlayActive = false;
					}
				});
			}
		}
	}

	@action
	openInDesigner(): void {
		if (this.currentUserService.handleDemo()) {
			return;
		}

		this.analyticsService.event(TrackingEvent.DATABOARD_EDIT, {
			databoard_id: this.args.model.id,
			origin: this.trackOrigin,
		});
		this.editBoard();
	}

	@action
	scheduleCheckIfTitleTooLong(node: HTMLElement) {
		schedule('afterRender', this, this.checkIfTitleTooLong, node);
	}
	<template>
		<li
			data-id={{@model.id}}
			class={{joinClasses
				(array
					"card"
					"card--preview"
					"card--board"
					"flex-item"
					"filter-sort-item"
					@model.animationClass
					(if this.isSomethingOpen "card--show-overlay")
					(if @hideSort "hideSort")
					(if this.isSelected "selected")
				)
			}}
		>

			<BoardImagePreviewComponent
				@image={{@model.image}}
				@color={{@model.color}}
				@theme={{@model.bgTheme}}
				@isVisibleOnScreen={{this.isVisibleOnScreen}}
				@isLazyLoaded={{true}}
			/>

			{{#if this.isVisibleOnScreen}}

				<div class="overlay">

					<div class="overlay-bg"></div>

					{{#if this.showDragHandle}}
						<div class="drag" data-id={{@model.id}}>
							<CommonUiIconComponent @name="ArrowsMove" @size="md" />
						</div>
					{{/if}}

					<div class="wrap">
						<div class="actions">
							<div class="actions__top">
								{{#if this.isDeleted}}
									{{#if this.canCurrentUserEditWithoutDeleted}}
										<SubmitButtonComponent
											@text="Restore"
											@onTrigger={{this.restore}}
											@noDelay={{true}}
										/>
									{{/if}}
								{{else}}
									<SubmitButtonComponent
										@text="View"
										@onTrigger={{this.openPreview}}
										@noDelay={{true}}
									/>

									{{#if this.canCurrentUserEdit}}
										<SubmitButtonComponent
											@text="Edit"
											@icon="Pencil"
											@onTrigger={{this.openInDesigner}}
											@noDelay={{true}}
											@theme="grey-outlined"
										/>
									{{/if}}
								{{/if}}
							</div>
							{{#unless this.isDeleted}}
								{{#if this.showAiChatButton}}
									<div class="actions__bottom">
										<CommonGenieButtonComponent
											@theme="dark"
											@openChat={{this.openGenie}}
										/>
									</div>
								{{else if this.canCurrentUserEdit}}
									<div class="actions__bottom">
										<SubmitButtonComponent
											@text="Share"
											@icon="Share"
											@onTrigger={{this.openWallShareSettings}}
											@noDelay={{true}}
											@theme="grey-outlined"
										/>
									</div>
								{{/if}}
							{{/unless}}
						</div>
					</div>

				</div>

				<div class="top">
					<div class="top__left">
						{{#if @batchSelect}}
							<div class="checkbox-wrapper">
								<FormCheckboxComponent
									@name="boards-grid-item"
									@selected={{this.isSelected}}
									@theme="blue"
									@onChange={{this.selectItem}}
								/>
							</div>
						{{/if}}

						{{#if @model.isPrivate}}
							<div role="button" class="private {{this.privateTooltipClass}}">
								<CommonUiIconComponent @name="Lock" @size="xs" />
								<ToolTipComponent
									@text={{this.privateTooltipText}}
									@interactive={{true}}
								/>
							</div>
						{{/if}}

						{{#if this.emblemTypeName}}
							<div class="emblem emblem--{{this.emblemTypeName}}"></div>
						{{/if}}

					</div>

					{{#if this.showActions}}
						<div class="top__right">
							{{#if @isRestricted}}
								<TeamsViewSectionRestrictedIconComponent />
							{{/if}}
							{{#unless this.isDeleted}}
								<DataPerformanceSummaryButtonComponent
									@object={{this.dataPerformanceSummaryObject}}
									@onPopupOpenStateChange={{this.changeDataPerformanceSummaryPopupOpenState}}
									@trackingOrigin="databoards"
								/>
								<VerificationBadgeComponent
									@verification={{@model.verificationInfo}}
									@entityLabel="databoard"
									@size="sm"
								/>
								<div
									role="button"
									class="favorite"
									{{on "click" this.toggleFavorite}}
								>
									<CommonUiIconComponent
										@name="StarFill"
										@size="sm"
										@class={{unless @model.favorite "gray"}}
									/>
									<ToolTipComponent @text={{@model.favoriteText}} />
								</div>
							{{/unless}}
							<div
								role="button"
								{{on "click" this.openAdditionalOptions}}
								class="context"
							>
								<CommonUiIconComponent @name="Ellipsis" @size="lg" />
							</div>
						</div>
					{{/if}}

				</div>

				<div class="info">

					<div class="anti-stretch">
						<EditableModelPropertyComponent
							@model={{@model}}
							@canEdit={{this.canEditBoardName}}
							@editableProperty="name"
						/>
					</div>
					{{#if @model.isGA4Migratable}}
						<a
							role="button"
							class="ga4-migration-link"
							{{on "click" this.openMigrationTool}}
						>
							<CommonUiIconComponent @name="Sparkles" @size="sm" />
							Transition to GA4
						</a>
					{{/if}}
				</div>

				<div class="bottom">
					{{#if @model.isAnythingToSolve}}
						{{#if this.showResolveButton}}
							<div class="bottom__stretch">
								<SourceConnectButtonComponent
									@sourceType={{null}}
									@onConnect={{this.resolveSources}}
									@text={{this.greenbuttonText}}
									@icon={{if @model.isOnlyConnect "Connect" "Reauthenticate"}}
									@theme="green"
								/>
							</div>
						{{/if}}
					{{else}}
						{{#if this.isError}}
							{{#if this.showResolveButton}}
								<div class={{@model.statusClass}}>
									<CommonUiIconComponent @name="AlertFill" @size="xs" />
									<span role="button" {{on "click" this.resolveBoardConnections}}>
										{{@model.statusText}}
									</span>
									<ToolTipComponent @text="Resolve" />
								</div>
							{{/if}}
						{{else}}
							<OwnerSelectComponent
								@model={{@model}}
								@canEdit={{this.canCurrentUserEdit}}
							/>
						{{/if}}

						<TagContainerComponent
							@model={{@model}}
							@canEdit={{this.canCurrentUserEdit}}
						/>

					{{/if}}
				</div>

			{{else}}
				<div class="info" {{visibleCheckItem this.visibleChanged this.checkVisibility}}>
					<div class="anti-stretch">&nbsp;</div>
				</div>
			{{/if}}

		</li>
	</template>
}
